import { addUser, inviteUser, listUsage, listUsers, removeUser, type UsageRow, type UserRow } from '../api';
import { resetPassword } from '../auth';

export function renderAdmin(host: HTMLElement) {
  host.innerHTML = `
    <section class="panel">
      <h3>Allowlisted users</h3>
      <table class="users" id="usersTable">
        <thead>
          <tr><th>Email</th><th>Role</th><th>Added</th><th>Generations</th><th>Spend</th><th></th></tr>
        </thead>
        <tbody><tr><td colspan="6" style="color:var(--text-dim);padding:14px;">Loading…</td></tr></tbody>
      </table>
      <div class="add-user">
        <input id="newEmail" type="email" placeholder="someone@example.com" />
        <select id="newRole">
          <option value="user">user</option>
          <option value="admin">admin</option>
        </select>
        <button class="btn" id="addBtn">Add</button>
        <button class="btn btn-secondary" id="inviteBtn"
          title="Allowlist + create their account and email them a password-setup link">Add &amp; invite</button>
      </div>
      <div id="inviteResult" style="margin-top:10px;"></div>
      <div id="adminErr" style="margin-top:10px;"></div>
    </section>
  `;

  const $ = <T extends HTMLElement = HTMLElement>(id: string) => host.querySelector<T>(`#${id}`)!;
  const tbody = $('usersTable').querySelector('tbody')!;
  const errBox = $('adminErr');

  async function refresh() {
    try {
      const [users, usage] = await Promise.all([listUsers(), listUsage()]);
      const usageMap = new Map(usage.map((u: UsageRow) => [u.email.toLowerCase(), u]));
      if (!users.length) {
        tbody.innerHTML = `<tr><td colspan="6" style="color:var(--text-dim);padding:14px;">No users yet.</td></tr>`;
        return;
      }
      tbody.innerHTML = users.map((u: UserRow) => {
        const usg = usageMap.get(u.email.toLowerCase());
        return `
          <tr>
            <td>${escapeHtml(u.email)}</td>
            <td class="role-${u.role}">${u.role}</td>
            <td style="color:var(--text-dim)">${new Date(u.addedAt).toLocaleDateString()}</td>
            <td>${usg?.totalGenerations ?? 0}</td>
            <td>$${(usg?.totalCost ?? 0).toFixed(2)}</td>
            <td><button class="btn btn-secondary" data-remove="${escapeAttr(u.email)}">Remove</button></td>
          </tr>
        `;
      }).join('');
      tbody.querySelectorAll<HTMLButtonElement>('[data-remove]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const email = btn.dataset.remove!;
          if (!confirm(`Remove ${email} from the allowlist?`)) return;
          btn.disabled = true;
          try { await removeUser(email); await refresh(); }
          catch (e) { showError(e); btn.disabled = false; }
        });
      });
    } catch (e) {
      showError(e);
    }
  }

  $<HTMLButtonElement>('addBtn').addEventListener('click', async () => {
    const email = $<HTMLInputElement>('newEmail').value.trim().toLowerCase();
    const role = $<HTMLSelectElement>('newRole').value as 'admin' | 'user';
    if (!email || !email.includes('@')) { showError(new Error('Valid email required')); return; }
    try {
      await addUser(email, role);
      $<HTMLInputElement>('newEmail').value = '';
      errBox.innerHTML = '';
      await refresh();
    } catch (e) { showError(e); }
  });

  // Invite flow ([PHASE 2] XDKuaRjc): allowlist + create the Auth account with
  // a temp password (proxy), then send the password-setup email from the
  // client SDK. The temp password is surfaced ONCE for the case the email
  // never arrives; it lives only in this admin's browser until dismissed.
  const inviteBtn = $<HTMLButtonElement>('inviteBtn');
  const inviteResult = $('inviteResult');
  const roleSelect = $<HTMLSelectElement>('newRole');
  const syncInviteAvailability = () => {
    const isUser = roleSelect.value === 'user';
    inviteBtn.disabled = !isUser;
    inviteBtn.title = isUser
      ? 'Allowlist + create their account and email them a password-setup link'
      : 'Invites are for the user role — add admins with the Add button';
  };
  roleSelect.addEventListener('change', syncInviteAvailability);
  syncInviteAvailability();

  inviteBtn.addEventListener('click', async () => {
    const email = $<HTMLInputElement>('newEmail').value.trim().toLowerCase();
    if (!email || !email.includes('@')) { showError(new Error('Valid email required')); return; }
    inviteBtn.disabled = true;
    const originalLabel = inviteBtn.textContent;
    inviteBtn.textContent = 'Inviting…';
    try {
      const { tempPassword } = await inviteUser(email);
      let emailSent = true;
      try { await resetPassword(email); } catch { emailSent = false; }
      renderInviteResult(email, tempPassword, emailSent);
      $<HTMLInputElement>('newEmail').value = '';
      errBox.innerHTML = '';
      await refresh();
    } catch (e) { showError(e); }
    finally {
      inviteBtn.textContent = originalLabel;
      syncInviteAvailability();
    }
  });

  function renderInviteResult(email: string, tempPassword: string, emailSent: boolean) {
    const status = emailSent
      ? `A password-setup email was sent to <strong>${escapeHtml(email)}</strong> so they can choose their own password.`
      : `⚠️ The password-setup email to <strong>${escapeHtml(email)}</strong> could not be sent — share the temp password below over a secure channel and have them use “Forgot password?” to replace it.`;
    inviteResult.innerHTML = `
      <div style="border:1px solid var(--border); border-radius:8px; padding:12px; background:var(--panel-2);">
        <div>${status}</div>
        <div style="margin-top:8px; display:flex; align-items:center; gap:8px; flex-wrap:wrap;">
          <span style="color:var(--text-dim)">One-time temp password (shown only now):</span>
          <code id="tempPw">${escapeHtml(tempPassword)}</code>
          <button class="btn btn-secondary" id="copyTempPw">Copy</button>
          <button class="btn btn-secondary" id="dismissTempPw">Dismiss</button>
        </div>
      </div>
    `;
    inviteResult.querySelector<HTMLButtonElement>('#copyTempPw')!.addEventListener('click', async (ev) => {
      const btn = ev.currentTarget as HTMLButtonElement;
      try {
        await navigator.clipboard.writeText(tempPassword);
        btn.textContent = 'Copied!';
        setTimeout(() => { btn.textContent = 'Copy'; }, 1500);
      } catch { showError(new Error('Clipboard unavailable — select and copy the password manually.')); }
    });
    inviteResult.querySelector<HTMLButtonElement>('#dismissTempPw')!.addEventListener('click', () => {
      inviteResult.innerHTML = '';
    });
  }

  function showError(e: unknown) {
    const msg = e instanceof Error ? e.message : String(e);
    errBox.innerHTML = `<div class="error">${escapeHtml(msg)}</div>`;
  }

  refresh();
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]!));
}
function escapeAttr(s: string): string { return escapeHtml(s); }
